import type { CachedCaster } from './types';
import { refuseUnported } from '../janus-engine/unported';
import { displayRefusal } from './k5-runtime';

const requests = new Map<string, Promise<CachedCaster>>();
export function displayCasterFromCache(id: string): CachedCaster | undefined {
    return (window.FateTide?.casterCache?.get(id)
        ?? (window.FateTide?.objectCache as Map<string, unknown> | undefined)?.get(id)) as CachedCaster | undefined;
}

/** A swallowed fetch failure is still a refusal. Keep it for this page until
 * a real cache seed arrives, so each heartbeat/repaint cannot refetch the PC. */
export function fetchDisplayCaster(id: string): Promise<CachedCaster> {
    const held = displayCasterFromCache(id);
    if (held) { requests.delete(id); return Promise.resolve(held); }
    const pending = requests.get(id);
    if (pending) return pending;
    const request = Promise.resolve().then(async () => {
        if (typeof fetchAndCacheCaster === 'function') await fetchAndCacheCaster(id);
        const fresh = displayCasterFromCache(id);
        if (!fresh) throw new Error(`unported:creature-display:caster-not-held:${id}`);
        return fresh;
    }).catch(() => {
        const reason = `unported:creature-display:caster-not-held:${id}`;
        refuseUnported(reason);
        throw new Error(reason);
    });
    requests.set(id, request);
    return request;
}

/** Roster projections are already computed. Only cold cells need a caster read;
 * a late failed read must never erase a value installed by a newer projection. */
export function mountCreatureStats(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>('[data-ft-creature-stat][data-ft-caster-id]').forEach(el => {
        const id = el.dataset.ftCasterId ?? '';
        const known = (): boolean => el.dataset.ftStatComputed === '1';
        const refuse = (error: unknown): void => { if (!known()) el.innerHTML = displayRefusal(error); };
        const paint = (): void => {
            const row = displayCasterFromCache(id), key = el.dataset.ftCreatureStat ?? '';
            const value = row ? ftLayerValue(row, key) : undefined;
            if (value == null) refuse(`unported:creature-display:stat-not-held:${key}`);
            else { el.textContent = String(value); el.dataset.ftStatComputed = '1'; }
        };
        if (displayCasterFromCache(id)) paint();
        else if (!known() && el.dataset.ftStatLoading !== '1') {
            el.dataset.ftStatLoading = '1';
            void fetchDisplayCaster(id).then(paint).catch(refuse).finally(() => { delete el.dataset.ftStatLoading; });
        }
    });
}
